import React, { useState, useRef, useMemo } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { CalendarForm } from '../components/calendar';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { spacing } from '../theme';

export const CalendarCreateScreen: React.FC = () => {
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const scope: 'business' | 'personal' = routeParams?.scope === 'personal' ? 'personal' : 'business';

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // A business calendar starts from the business's timezone, working days
  // and hours (set in onboarding / Business profile).
  const businessDefaults = useMemo(() => {
    if (scope !== 'business' || !currentBusiness) return undefined;
    const b: any = currentBusiness;
    const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const open = order.map((day, index) => ({ index, hours: b.workingHours?.[day] })).filter((d) => d.hours?.isWorking);
    const first = open[0]?.hours;
    return {
      ...(b.timezone ? { timezone: b.timezone } : {}),
      ...(open.length && first?.start && first?.end
        ? { availability: { mode: 'customized_days' as const, daysOfWeek: open.map((d) => d.index), isAllDay: false, timeRange: { startTime: first.start, endTime: first.end }, maxBookingsPerSlot: 1 } }
        : {}),
    };
  }, [scope, currentBusiness?._id]);

  const handleSubmit = async (formData: any) => {
    if (scope === 'business' && !currentBusiness?._id) {
      showNotification('error', 'Error', t('calendar.create.businessRequired'));
      return;
    }
    if (scope === 'personal' && !user?.id) {
      showNotification('error', 'Error', t('calendar.create.userRequired'));
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.createCalendar(
        scope === 'personal'
          ? { userId: user!.id, ...formData }
          : { businessId: currentBusiness!._id, ...formData }
      );

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.create.successMessage'));
        navigateTo('calendars');
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.create.failedMessage');
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating calendar:', error);
      showNotification('error', 'Error', error.message || t('calendar.create.failedMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('calendars');
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title={scope === 'personal' ? t('calendar.create.personalTitle') : t('calendar.create.title')}
          subtitle={scope === 'personal' ? t('calendar.create.personalSubtitle') : t('calendar.create.subtitle')}
          onBack={handleCancel}
          actions={[
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading,
              disabled: loading,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.formContainer}>
            <CalendarForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              submitLabel={t('calendar.create.submitLabel')}
              initialData={businessDefaults as any}
              loading={loading}
              businessId={scope === 'business' ? currentBusiness?._id || '' : ''}
              userId={scope === 'personal' ? user?.id || '' : ''}
              hideActions={true}
              submitRef={formSubmitRef}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  formContainer: {
    padding: spacing.md,
  },
});

